import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useCountUp } from 'use-count-up';

import { getMattressFirmnessByErgonomicIndex, mapRange } from '../../helpers';
import { getRecommendationTexts, getTopperRecommendation } from './helpers';
import { useStore } from "@/features/bed-configurator/store";

import Icon from '@/components/Icon';
import Button from '@/components/Button';
import useIntersection from '@/hooks/useIntersection';
import gelTopper from '../../../../assets/img/dsf-result-gel-topper.png';

import Carousel, { useCarousel } from '../../components/Carousel';

import styles from './index.module.scss';
import { Data, UserData } from '../../types/Data';
import { useQuiz, useQuizData } from '../../contexts/QuizContext';

interface ResultScreenProps {
    closeModal: () => void
    onUseRecommendations: (attributes: any) => void
    embed: boolean
}

export default function ResultScreen ({
    closeModal,
    onUseRecommendations,
    embed,
}: ResultScreenProps) {

    const { setData } = useQuiz(),
        { quizData } = useQuizData(),
        store = useStore();

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // calculate score and firmness

        quizData.you = calculateData(quizData.you);

        if (quizData.partner) {
            quizData.partner = calculateData(quizData.partner)
        }

        setData((data) => ({
            ...data,
            quizData: {
                ...quizData
            }
        }));

        setLoading(false);
    }, []);

    if (loading) {
        return <p>Loading</p>;
    }

    const topperRecommendation = getTopperRecommendation(quizData);

    function setRecommendations (data: Data) {
        const {you, partner} = data;

        // set 'you' recommendations
        const attributes = {
            mattress_hardness_1: you.firmness,
            mattress_hardness_2: you.firmness,
            topper: topperRecommendation === 'Kaltschaum' ? 'kaltschaum' : (store && store.attributes.series === 'mozart9') ? 'puregel' : 'gel',
        };

        // set 'partner' recommendations
        if (partner) {
            attributes.mattress_hardness_2 = partner.firmness;
        }

        closeModal();

        onUseRecommendations(attributes);
    }

    return (
        <div className={styles.content}>

            <h2>{quizData.partner ? 'Eure' : 'Deine'} Empfehlungen auf Basis der Deep-Sleep-Formel von Mozart</h2>

            <div className={classNames(styles.results, {
                [styles.resultsWithPartner]: quizData.partner,
            })}>
                {quizData.partner ? (
                    <ResultsWithPartner data={quizData} />
                ) : (
                    <ResultsAlone data={quizData.you} />
                )}

                <div className={classNames(styles.result, styles.topperRecommendation)}>
                    <h3>
                        {quizData.partner ? 'Eure' : 'Deine'} Topper-Empfehlung: {topperRecommendation}
                    </h3>

                    <div className={styles.toppers}>
                        <div className={classNames(styles.topper, styles.active)}>
                            <img src={gelTopper} alt="Gel topper" />
                        </div>
                    </div>

                    <div className={styles.info}>
                        <p>
                            Eine ergonomische Schlafposition ist durch die Mozart 7-Zonen Matratze samt richtigen Härtegrad
                            sichergestellt. Damit {quizData.partner ? 'Ihr Euch' : 'Du Dich'} im neuen Bett auch richtig {quizData.partner ? 'wohlfühlt' : 'wohlfühlst'},
                            rundet der {topperRecommendation === 'Kaltschaum' ? 'Kaltschaum-Topper' : 'Gel-Topper'} {quizData.partner ? 'Euer' : 'Dein'} bevorzugtes
                            Liegegefühl perfekt ab.
                        </p>
                    </div>
                </div>
            </div>

            <div className={styles.buttons}>
                <Button type='secondary' onClick={() => closeModal()} abClassName="Schließen 3">Schließen</Button>
                <Button className={styles.useRecommendationsButton} onClick={() => setRecommendations(quizData)} abClassName={`${ embed ? 'Jetzt Traum-Bett konfigurieren' : 'Empfehlungen übernehmen' }`}>
                    { embed ? 'Jetzt Traum-Bett konfigurieren' : 'Empfehlungen übernehmen' }
                    <Icon icon='arrow-right' size={18} />
                </Button>
            </div>

            <div className={styles.info}>
                <h5>Angst vor der falschen Wahl?</h5>
                <p>
                    Dazu gibt es keinen Grund 😊 Ganze 94% unserer Kunden sind mit ihrer Empfehlung sofort
                    zufrieden. Und für alle anderen bieten wir die Möglichkeit eines kostenlosen Matratzentauschs.
                    Solltest Du mit dem Härtegrad nicht zufrieden sein, liefern wir Dir eine neue Matratze und
                    nehmen die alte wieder mit - für Dich 100% kostenlos!
                </p>
            </div>

        </div>
    );
}


interface ResultProps {
    data: UserData
    inViewport: boolean
    setRendered?: (rendered: boolean) => void
    rendered?: boolean
}

function Result ({
    data,
    inViewport,
    setRendered,
    rendered,
}: ResultProps) {

    const { data: { doubleBed } } = useQuiz();

    const { score } = data;

    const { value } = useCountUp({
        isCounting: inViewport,
        start: rendered ? score : 0,
        end: score,
        duration: 4,
        onComplete() {
            setRendered && setRendered(true);
        },
    });

    const rotationH2 = mapRange(score, 0, 48, -140, -80),
        rotationH3 = mapRange(score, 49, 62, -45, 45),
        rotationH4 = mapRange(score, 63, 90, 80, 140);

    const rotation = score <= 48 ? rotationH2 : (score <= 62 ? rotationH3 : Math.min(rotationH4, 140));
    const fm = getMattressFirmnessByErgonomicIndex(value as number);

    return (
        <div className={styles.score}>
            <div className={styles.circle}>
                <svg viewBox="0 0 254 254" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M76.1745 232.375C73.7449 237.409 67.6716 239.554 62.8496 236.73C41.079 223.982 23.5236 204.989 12.5315 182.103C0.0741692 156.167 -3.21552 126.769 3.20081 98.7182C9.61714 70.6679 25.3595 45.6272 47.8518 27.6939C70.344 9.76058 98.2541 -0.00332249 127.015 8.2559e-06C155.775 0.003339 183.683 9.77371 206.171 27.7122C228.659 45.6508 244.396 70.6951 250.806 98.7469C257.216 126.799 253.919 156.197 241.456 182.13C230.458 205.013 212.899 224.002 191.125 236.745C186.302 239.567 180.23 237.421 177.801 232.387C175.373 227.352 177.514 221.342 182.294 218.447C199.958 207.747 214.21 192.107 223.221 173.357C233.699 151.556 236.47 126.841 231.081 103.259C225.693 79.6759 212.463 58.6215 193.558 43.5409C174.653 28.4602 151.191 20.2464 127.012 20.2436C102.834 20.2408 79.3702 28.4492 60.4613 43.5255C41.5525 58.6017 28.3181 79.653 22.924 103.234C17.5299 126.816 20.2955 151.531 30.7682 173.335C39.7748 192.087 54.0229 207.73 71.6848 218.434C76.4638 221.33 78.604 227.341 76.1745 232.375Z" fill="#00D4E2"/>
                    <path d="M76.1745 232.375C73.7449 237.409 67.6716 239.554 62.8496 236.73C48.1871 228.144 35.3648 216.683 25.1714 203C12.9584 186.605 4.88031 167.503 1.62323 147.317C-1.63385 127.13 0.0262647 106.456 6.46259 87.0485C12.8989 67.6414 23.9213 50.075 38.5938 35.8411C53.2662 21.6072 71.1553 11.1264 90.7417 5.2886C110.328 -0.549179 131.033 -1.57143 151.099 2.30866C171.165 6.18875 189.998 14.8566 206 27.576C219.356 38.1921 230.41 51.3689 238.535 66.2969C241.207 71.2063 238.877 77.2133 233.773 79.4876C228.669 81.762 222.73 79.4365 219.984 74.568C213.233 62.5981 204.22 52.0153 193.414 43.4263C179.962 32.7333 164.129 25.4464 147.26 22.1845C130.391 18.9226 112.984 19.7819 96.5182 24.6897C80.0522 29.5974 65.0132 38.4085 52.6783 50.3747C40.3434 62.3408 31.077 77.1087 25.6661 93.4239C20.2552 109.739 18.8596 127.12 21.5978 144.09C24.3359 161.061 31.127 177.119 41.3943 190.902C49.6413 201.973 59.9351 211.313 71.6848 218.434C76.4638 221.33 78.604 227.341 76.1745 232.375Z" fill="#000FE2"/>
                    <path d="M76.1745 232.375C73.7449 237.409 67.6716 239.554 62.8496 236.73C50.9476 229.761 40.24 220.886 31.1618 210.44C19.867 197.443 11.3309 182.285 6.07193 165.886C0.81299 149.486 -1.05897 132.189 0.56978 115.043C1.87888 101.262 5.42753 87.8114 11.055 75.2147C13.3349 70.1112 19.5215 68.3207 24.4247 71.0007C29.328 73.6807 31.083 79.8152 28.8803 84.9526C24.5235 95.1142 21.7617 105.91 20.7121 116.959C19.3429 131.372 20.9166 145.914 25.3377 159.701C29.7588 173.488 36.935 186.231 46.4304 197.157C53.7085 205.532 62.2323 212.705 71.6848 218.434C76.4638 221.33 78.604 227.341 76.1745 232.375Z" fill="#46AFFF"/>
                </svg>

                <span className={classNames(styles.stepH2, {
                    [styles.active]: fm === 'h2',
                })}>H2</span>
                <span className={classNames(styles.stepH3, {
                    [styles.active]: fm === 'h3',
                })}>H3</span>
                <span className={classNames(styles.stepH4, {
                    [styles.active]: fm === 'h4',
                })}>H4</span>

                <div className={styles.arrow} style={{ '--rotate': `${rotation}deg` } as CSSProperties}>
                    <svg width="26" height="23" viewBox="0 0 26 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.4112 1C12.181 -0.33333 14.1056 -0.333334 14.8754 1L25.3918 19.2149C26.1616 20.5483 25.1993 22.215 23.6597 22.215H2.6269C1.0873 22.215 0.125043 20.5483 0.894843 19.215L11.4112 1Z" fill="white"/>
                    </svg>
                </div>

                <div className={classNames(styles.value, styles[fm])} style={{ '--rotate': `${rotation}deg` } as CSSProperties}>
                    <strong>{fm.toUpperCase()}</strong>
                    <span>
                        Ergonomischer Härtegrad-Index: {value}&nbsp;
                        <span
                            className={styles.tooltip}
                            data-tooltip={`Der ergonomische Härtegrad-Index von Mozart empfiehlt anhand von verschiedenen Körper- und Schlafparametern den aus ergonomischer Sicht am besten geeignetsten Härtegrad für dauerhaft gesunden und erholsamen Schlaf. ${doubleBed ? `Klicke auf “Warum wir ${data.firmness.toUpperCase()} empfehlen”, um die detaillierte, individuelle Analyse zu erhalten.` : ''}`}
                        >
                            <Icon icon='info' size={16}/>
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );
}

function calculateData (data: UserData) {
    const bmi = (data.weight / ((data.height / 100) ** 2));

    // calculate ergonomic index
    let multiplicators = 1;

    switch (data.sleepPosition) {
        case 'auf der Seite':
            multiplicators *= 0.9;
            break;
        case 'auf dem Bauch':
            multiplicators *= 1.1;
            break;
        case 'auf dem Rücken':
            multiplicators *= 1.07;
            break;
        case 'häufig wechselnd':
            multiplicators *= 1.05;
            break;
        case 'Weiß ich nicht genau':
            multiplicators *= 1;
            break;
    }

    switch (data.body) {
        case 'normal':
            multiplicators *= 1;
            break;
        case 'schlank':
            multiplicators *= 1;
            break;
        case 'kurvig':
            multiplicators *= 0.96;
            break;
    }

    const ergonomicIndex = (bmi * multiplicators * 2) | 0;
    const firmness = getMattressFirmnessByErgonomicIndex(ergonomicIndex);

    data.bmi = bmi;
    data.score = ergonomicIndex;
    data.firmness = firmness;

    return data;
}


function ResultsWithPartner ({data}) {

    return (
        <>
            <ResultsItem data={data.you} title='Deine Härtegrad-Empfehlung' />
            <ResultsItem data={data.partner} title='Härtegrad-Empfehlung für Dein(e) PartnerIn' />
        </>
    )
}

function ResultsItem ({title, data}) {
    const [screen, setScreen, direction] = useCarousel(),
        recommendations = useMemo(() => getRecommendationTexts(data), [data]);

    const screenRef = useRef(null);

    const [rendered, setRendered] = useState(false);

    const [wasInViewport, setWasInViewport] = useState(false);
    const inViewport = useIntersection(screenRef, '-100px');

    useEffect(() => {
        if (inViewport && !wasInViewport) {
            setWasInViewport(true);
        }
    }, [inViewport, wasInViewport]);

    return (
        <Carousel
            className={styles.result}
            title={title}
            screens={[
                {
                    screen: (
                        <div className={classNames(styles.mainScreen, {
                            [styles.visible]: wasInViewport && !rendered,
                            [styles.rendered]: rendered,
                        })} ref={screenRef}>
                            <Result data={data} inViewport={wasInViewport} setRendered={setRendered} rendered={rendered} />
                            <button className={styles.button} onClick={() => setScreen((screen) => screen + 1)}>
                                Warum wir {data.firmness.toUpperCase()} empfehlen
                                <Icon size={20} icon='chevron-right' />
                            </button>
                        </div>
                    ),
                    showButtons: false,
                    icon: (
                        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                            <path d="M18.75 12.5V14.375C18.75 14.7065 18.6183 15.0245 18.3839 15.2589C18.1495 15.4933 17.8315 15.625 17.5 15.625H9.05703C8.99939 15.6251 8.94286 15.6091 8.89369 15.5791C8.84452 15.549 8.80463 15.5059 8.77843 15.4545C8.75223 15.4032 8.74074 15.3456 8.74524 15.2881C8.74974 15.2307 8.77004 15.1756 8.80391 15.1289L13.6148 8.51563C13.7146 8.38317 13.7591 8.21725 13.7392 8.05266C13.7192 7.88808 13.6363 7.73761 13.5078 7.63282C13.4417 7.58153 13.366 7.54404 13.2851 7.52256C13.2043 7.50109 13.1199 7.49606 13.0371 7.50779C12.9542 7.51952 12.8746 7.54777 12.8029 7.59085C12.7311 7.63393 12.6688 7.69097 12.6195 7.7586L6.99453 15.4977C6.96505 15.5378 6.92641 15.5702 6.88184 15.5924C6.83727 15.6145 6.78805 15.6257 6.73828 15.625H2.5C2.16848 15.625 1.85054 15.4933 1.61612 15.2589C1.3817 15.0245 1.25 14.7065 1.25 14.375V12.5883C1.25 12.4484 1.25 12.3094 1.26016 12.1719C1.26417 12.0917 1.29886 12.0162 1.35703 11.9609C1.41521 11.9057 1.49241 11.8749 1.57266 11.875H3.75C3.83568 11.8752 3.92048 11.8578 3.99914 11.8238C4.0778 11.7898 4.14864 11.7401 4.20725 11.6776C4.26586 11.6151 4.311 11.5412 4.33986 11.4605C4.36871 11.3798 4.38068 11.2941 4.375 11.2086C4.36118 11.0476 4.28681 10.8979 4.1669 10.7895C4.04699 10.6812 3.89047 10.6224 3.72891 10.625H1.86875C1.82108 10.625 1.77404 10.614 1.73125 10.593C1.68846 10.572 1.65104 10.5415 1.62186 10.5038C1.59268 10.4661 1.57251 10.4222 1.5629 10.3755C1.55329 10.3288 1.55449 10.2805 1.56641 10.2344C2.50391 6.80938 5.44609 4.2086 9.02891 3.80626C9.07268 3.80152 9.11695 3.80609 9.15883 3.81966C9.20072 3.83323 9.23926 3.8555 9.27193 3.885C9.30461 3.91451 9.33068 3.95058 9.34844 3.99087C9.36621 4.03115 9.37526 4.07473 9.375 4.11876V6.25001C9.37481 6.33568 9.39224 6.42049 9.42621 6.49915C9.46017 6.57781 9.50995 6.64864 9.57245 6.70726C9.63494 6.76587 9.70882 6.811 9.78949 6.83986C9.87017 6.86872 9.95591 6.88068 10.0414 6.875C10.2024 6.86118 10.3522 6.78681 10.4605 6.66691C10.5688 6.547 10.6276 6.39047 10.625 6.22891V4.11485C10.6247 4.07082 10.6338 4.02724 10.6516 3.98696C10.6693 3.94668 10.6954 3.9106 10.7281 3.8811C10.7607 3.85159 10.7993 3.82933 10.8412 3.81575C10.883 3.80218 10.9273 3.79762 10.9711 3.80235C12.7203 3.9989 14.3697 4.71759 15.7046 5.86483C17.0396 7.01206 17.9981 8.53467 18.4555 10.2344C18.4674 10.2806 18.4686 10.3289 18.4589 10.3757C18.4493 10.4225 18.429 10.4664 18.3998 10.5041C18.3705 10.5418 18.333 10.5723 18.2901 10.5933C18.2472 10.6142 18.2001 10.6251 18.1523 10.625H16.2711C16.1098 10.6228 15.9537 10.6818 15.8341 10.7901C15.7145 10.8984 15.6404 11.0479 15.6266 11.2086C15.6209 11.2941 15.6328 11.3798 15.6617 11.4605C15.6906 11.5412 15.7357 11.6151 15.7943 11.6776C15.8529 11.7401 15.9238 11.7898 16.0024 11.8238C16.0811 11.8578 16.1659 11.8752 16.2516 11.875H18.4328C18.5138 11.875 18.5917 11.9064 18.65 11.9626C18.7083 12.0189 18.7425 12.0956 18.7453 12.1766C18.75 12.2836 18.75 12.3914 18.75 12.5Z" />
                        </svg>
                    )
                },
                ...recommendations,
            ]}
            screen={screen}
            setScreen={setScreen}
            direction={direction}
        />
    )
}

function ResultsAlone ({data}) {
    const [screen, setScreen, direction] = useCarousel();
    const recommendations = useMemo(() => getRecommendationTexts(data), [data]);

    return (
        <div className={classNames(styles.result, styles.resultSingle)}>
            <div className={classNames(styles.resultContent, styles.mainScreen, styles.visible)}>
                <Result data={data} inViewport rendered={false} />

                <Carousel screen={screen} setScreen={setScreen} direction={direction}
                    title='Deine Härtegrad-Empfehlung'
                    screens={recommendations}
                />
            </div>
        </div>
    );
}
